import useIsMobile from "hooks/useIsMobile";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { onAdminAccountChange } from "store/slices/user";
import "./ChatWidget.css";

const API_BASE_URL = (window as any)?.config?.apiUrl || "http://localhost:3000";

interface IChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface IChat {
  id: string;
  accountId: string;
  orderId?: string;
  chatModelId: number;
  mostRecentResponseId?: string;
  chatMessages?: IChatMessage[];
  createdAt: string;
  updatedAt: string;
  chatModel?: {
    id: number;
    multipleChatsSupported?: boolean;
    [key: string]: any;
  };
}

export type ChatBotType = "PatientChatBot" | "ContractManagementBot";

interface IChatWidgetProps {
  accountId: string;
  chatType: ChatBotType;
  shouldAutoOpen?: boolean;
  contextData?: any; // Flexible context data that depends on chat type
  title?: string;
  welcomeMessage?: string;
  position?: "fixed" | "inline";
}

export function ChatWidget({
  accountId,
  chatType,
  shouldAutoOpen = false,
  contextData,
  title,
  welcomeMessage,
  position = "fixed",
}: IChatWidgetProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [lastSelectedChatId, setLastSelectedChatId] = useState<string | null>(
    null
  );
  const [chats, setChats] = useState<IChat[]>([]);
  const [multipleChatsSupported, setMultipleChatsSupported] = useState<
    boolean | null
  >(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Get mobile detection hook
  const { isMobile } = useIsMobile();

  // Get auth token and account info from Redux store
  const { publicToken, token } = useSelector((state: any) => state.authSlice);
  const { currentAccount } = useSelector((state: any) => state.userSlice);
  const dispatch = useDispatch();

  // Function to update URL with chatId
  const updateUrlWithChatId = (chatId: string | null) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (chatId) {
      newSearchParams.set("chatId", chatId);
    } else {
      newSearchParams.delete("chatId");
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleChatSelect = (chat: IChat) => {
    setCurrentChatId(chat.id);
    setSelectedChatId(chat.id);
    setLastSelectedChatId(chat.id);
    setMessages(chat.chatMessages || []);
    updateUrlWithChatId(chat.id);
    // Focus the input when selecting a chat
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Function to open chat widget with specific chat
  const openChatWidget = (chatId?: string) => {
    setIsOpen(true);
    if (chatId) {
      // Find the chat and load it
      const chat = chats.find((c) => c.id === chatId);
      if (chat) {
        handleChatSelect(chat);
      }
    }
  };

  // Check for chatId in URL params on mount and when chats change
  useEffect(() => {
    const chatIdFromUrl = searchParams.get("chatId");
    if (chatIdFromUrl && chats.length > 0) {
      const chat = chats.find((c) => c.id === chatIdFromUrl);
      if (chat) {
        openChatWidget(chatIdFromUrl);
      }
    }
  }, [chats, searchParams]);

  // Auto-open chat widget if shouldAutoOpen prop is true
  useEffect(() => {
    if (shouldAutoOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldAutoOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // For contract management bots, ensure current account matches the account being viewed
  useEffect(() => {
    if (
      chatType === "ContractManagementBot" &&
      accountId &&
      currentAccount !== accountId
    ) {
      dispatch(onAdminAccountChange({ currentAccount: accountId }));
    }
  }, [chatType, accountId, currentAccount, dispatch]);

  const fetchChats = async () => {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Use appropriate token based on chat type
      if (chatType === "ContractManagementBot") {
        // Use regular token for internal contract management
        if (token) {
          headers.authorization = `Bearer ${token}`;
        }
        // Include account header for permission checks
        if (currentAccount) {
          headers.account = currentAccount;
        }
      } else if (publicToken) {
        // Use public token for patient-facing bots
        headers.authorization = `Bearer ${publicToken}`;
      }

      // Use different endpoints based on chat type
      let url;
      if (chatType === "ContractManagementBot") {
        // Use main endpoint for contract management (Auth0 auth)
        const queryParams = `accountId=${accountId}&chatType=${chatType}`;
        url = `${API_BASE_URL}/chats?${queryParams}`;
      } else {
        // Use patient endpoint for patient chat (patient auth)
        let queryParams = `accountId=${accountId}&chatType=${chatType}`;
        if (contextData?.orderExternalId) {
          queryParams += `&orderExternalId=${contextData.orderExternalId}`;
        }
        url = `${API_BASE_URL}/chats/patient?${queryParams}`;
      }

      const response = await fetch(url, {
        credentials: "include",
        headers,
      });
      const result = await response.json();

      // Both /chats and /chats/patient return { data: [...] }
      const chatList = result.data || [];

      setChats(chatList);

      // Set multipleChatsSupported based on the first chat's model configuration
      let chatSupportsMultiple = true;
      if (chatList.length > 0 && chatList[0].chatModel) {
        chatSupportsMultiple =
          chatList[0].chatModel.multipleChatsSupported ?? true;
        setMultipleChatsSupported(chatSupportsMultiple);
      }

      // If multiple chats not supported and we have chats, auto-select the most recent one
      if (!chatSupportsMultiple && chatList.length > 0 && !currentChatId) {
        const mostRecentChat = chatList[0]; // Already sorted by createdAt desc
        handleChatSelect(mostRecentChat);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchChats();
      // Focus the input when the widget opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, accountId, chatType]);

  // Restore last selected chat when widget opens and no chatId in URL
  useEffect(() => {
    if (
      isOpen &&
      chats.length > 0 &&
      lastSelectedChatId &&
      !searchParams.get("chatId")
    ) {
      const lastChat = chats.find((c) => c.id === lastSelectedChatId);
      if (lastChat) {
        handleChatSelect(lastChat);
      }
    }
  }, [isOpen, chats, lastSelectedChatId, searchParams]);

  const handleSendMessage = async (messageContent?: string) => {
    const messageToSend = messageContent || inputMessage.trim();
    if (!messageToSend || isLoading) return;

    const userMessage: IChatMessage = {
      role: "user",
      content: messageToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setInputMessage("");

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Use appropriate token based on chat type
      if (chatType === "ContractManagementBot") {
        // Use regular token for internal contract management
        if (token) {
          headers.authorization = `Bearer ${token}`;
        }
        // Include account header for permission checks
        if (currentAccount) {
          headers.account = currentAccount;
        }
      } else if (publicToken) {
        // Use public token for patient-facing bots
        headers.authorization = `Bearer ${publicToken}`;
      }

      // Use different send message endpoints based on chat type
      const sendMessageUrl =
        chatType === "ContractManagementBot"
          ? `${API_BASE_URL}/chats/send_message` // Auth0 auth
          : `${API_BASE_URL}/chats/patient/send_message`; // Patient auth

      const response = await fetch(sendMessageUrl, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          chatId: currentChatId,
          accountId,
          type: chatType,
          message: messageToSend,
          data: contextData,
        }),
      });

      const result = await response.json();
      const responseData = result.data;

      // If this was a new chat, set the chat ID and update URL
      if (!currentChatId) {
        setCurrentChatId(responseData.chatId);
        setSelectedChatId(responseData.chatId);
        setLastSelectedChatId(responseData.chatId);
        updateUrlWithChatId(responseData.chatId);
      }

      const assistantMessage: IChatMessage = {
        role: "assistant",
        content: responseData.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Refetch chats to update the list
      fetchChats();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsLoading(false);
      // Focus the input after sending a message
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    setSelectedChatId(null);
    setLastSelectedChatId(null);
    setMessages([]);
    updateUrlWithChatId(null);
    // Focus the input when starting a new chat
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea on mobile for better UX
  const handleTextareaResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    if (isMobile && e.target) {
      // Reset height to auto to get correct scrollHeight
      e.target.style.height = "auto";
      // Set height to scrollHeight but limit to max-height
      const maxHeight = 100; // Max height in pixels
      const newHeight = Math.min(e.target.scrollHeight, maxHeight);
      e.target.style.height = `${newHeight}px`;
    }
  };

  // Get default title and welcome message based on chat type
  const defaultTitle =
    chatType === "PatientChatBot"
      ? "Meet Clara, your AI Assistant"
      : "Contract Management Assistant";

  const defaultWelcomeMessage =
    chatType === "PatientChatBot"
      ? "👋 Hi! I'm Clara, I am learning to become an AI assistant. How can I help you today?"
      : "👋 Hi! I'm your Contract Management Assistant. I can help you with contract analysis, pricing insights, and negotiations. How can I assist you today?";

  // FAQ questions for patient chat
  const getFaqQuestions = () => {
    if (chatType === "PatientChatBot") {
      return [
        "Can you explain my bill in plain English?",
        "How is my responsibility calculated?",
      ];
    }
    if (chatType === "ContractManagementBot") {
      return [
        "What's my biggest revenue opportunity right now?",
        "Which payers should I prioritize for contract renegotiation?",
        "Give me specific cost saving recommendations based on my current data",
      ];
    }
    return [];
  };

  const faqQuestions = getFaqQuestions();

  const handleFaqClick = (question: string) => {
    handleSendMessage(question);
  };

  const displayTitle = title || defaultTitle;
  const displayWelcomeMessage = welcomeMessage || defaultWelcomeMessage;

  if (!isOpen) {
    return (
      <div
        className={`chat-widget-button ${
          position === "inline" ? "chat-widget-button-inline" : ""
        }`}
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => e.key === "Enter" && setIsOpen(true)}
      >
        <div className="chat-icon">💬</div>
        <span>Ask Clara</span>
      </div>
    );
  }

  return (
    <div
      className={`chat-widget ${
        position === "inline" ? "chat-widget-inline" : ""
      }`}
    >
      <div className="chat-header">
        <h3>{displayTitle}</h3>
        <button
          type="button"
          className="close-button"
          onClick={() => {
            setIsOpen(false);
            updateUrlWithChatId(null);
          }}
        >
          −
        </button>
      </div>

      <div className="chat-body">
        {/* Chat List Sidebar - Only show if multiple chats are supported */}
        {multipleChatsSupported === true && (
          <div className="chat-list">
            <div className="chat-list-header">
              <button
                type="button"
                className="new-chat-button"
                onClick={handleNewChat}
              >
                + New Chat
              </button>
            </div>
            <div className="chat-list-items">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-list-item ${
                    selectedChatId === chat.id ? "active" : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleChatSelect(chat)}
                  onKeyDown={(e) => e.key === "Enter" && handleChatSelect(chat)}
                >
                  <div className="chat-preview">
                    {chat.chatMessages && chat.chatMessages.length > 0
                      ? `${chat.chatMessages[0].content.slice(
                          0,
                          isMobile ? 30 : 50
                        )}...`
                      : "New conversation"}
                  </div>
                  <div className="chat-date">
                    {new Date(chat.createdAt).toLocaleDateString(
                      undefined,
                      isMobile ? { month: "short", day: "numeric" } : undefined
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Messages Area */}
        <div
          className={`chat-messages ${
            multipleChatsSupported !== true ? "full-width" : ""
          }`}
        >
          <div className="messages-container">
            {messages.length === 0 && (
              <>
                <div className="welcome-message">{displayWelcomeMessage}</div>
                {faqQuestions.length > 0 && (
                  <div className="faq-container">
                    <div className="faq-questions">
                      {faqQuestions.map((question, index) => (
                        <button
                          key={index}
                          type="button"
                          className="faq-button"
                          onClick={() => handleFaqClick(question)}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`message ${
                  message.role === "user" ? "user-message" : "assistant-message"
                }`}
              >
                <div
                  className="message-content"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {message.content}
                </div>
                <div className="message-time">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant-message">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="chat-input">
            <div className="input-controls">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleTextareaResize}
                onKeyPress={handleKeyPress}
                placeholder="Ask your question"
                rows={1}
                disabled={isLoading}
                style={isMobile ? { resize: "none", overflow: "hidden" } : {}}
              />
              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="send-button"
              >
                Send
              </button>
            </div>
            <div className="chat-disclaimer">
              AI assistant can make mistakes. If unsure, call us.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
